import { min, minBy, max, maxBy, find, findIndex, range } from 'lodash'
import addNumberFieldToPlayersAndReorder from '../utils/addNumberFieldToPlayersAndReorder'
import colorByNumber from '../utils/colorByNumber'

export default function enhancer(input, myUuid) {
    const { data = {} } = input
    let players = addNumberFieldToPlayersAndReorder(data.players || []).map(player => {
        const timesPerSegment = (player.timesPerSegment || []).map(({ time, pitstop }) => ({ time: (typeof time === 'number' && time > 0 ? time : null), pitstop })) // null values if it is 'out'
        const playerFastLap = min(timesPerSegment.map(({ time }) => time || Infinity)) || Infinity
        const playerFastLapIndex = findIndex(timesPerSegment, ({ time }) => time === playerFastLap)
        const retiredSegmentIndex = findIndex(timesPerSegment, ({ time }) => time === null)
        const segments = timesPerSegment.reduce((result, { time, pitstop }, segmentIndex) => {
            const lastSection = result.length > 0 ? result[result.length - 1] : null
            const isRunning = time !== null
            return [
                ...result,
                {
                    lap: segmentIndex + 1,
                    time,
                    pitstop,
                    accumulated: (lastSection ? lastSection.accumulated : 0) + (isRunning ? time : 0),
                    playerFastLapDelta: isRunning ? time - playerFastLap : null,
                    isPlayersFastLap: segmentIndex === playerFastLapIndex,
                    isRunning,
                    isOut: !isRunning,
                    hasRetiredInThisSegment: segmentIndex === retiredSegmentIndex,
                }
            ]
        }, [])
        return {
            ...player,
            color: colorByNumber(player.number),
            isMe: player.uuid === myUuid || player.id === myUuid || player.uuid === 'ME' || player.id === 'ME',
            time: typeof player.time === 'number' ? player.time : null,
            accumulatedTime: segments.length > 0 ? segments[segments.length - 1].accumulated : 0,
            segments,
            playerFastLap
        }
    })
    const winner = find(players, p => p.position === 1)
    const fastLap = minBy(players, p => p.playerFastLap).playerFastLap
    const raceTime = maxBy(players, p => p.accumulatedTime).accumulatedTime
    players = players.map((player, playerIndex) => {
        const fastLapIndex = findIndex(player.segments, s => s.time === fastLap)
        return {
            ...player,
            segments: player.segments.map((segment, segmentIndex) => ({
                ...segment,
                differenceVsWinner: segment.accumulated - winner.segments[segmentIndex].accumulated,
                fastLapDelta: segment.time - fastLap,
                isFastLap: segmentIndex === fastLapIndex
            }))
        }
    })

    // Calculates positions and delta info per segment (independently)
    const numberOfSegments = players.length > 0 ? players[0].segments.length : 0
    const fastestAndSlowerPerSegment = range(0, numberOfSegments).map(i => {
        const timesThisSegment = players.map(p => p.segments[i]).filter(s => s.isRunning).map(s => s.time)
        const fastest = min(timesThisSegment) || 0
        const slowest = max(timesThisSegment) || 0
        const timesAccumulatedThisSegment = players.map(p => p.segments[i]).filter(s => s.isRunning).map(s => s.accumulated)
        const fastestAccumulated = min(timesAccumulatedThisSegment) || 0
        const slowestAccumulated = max(timesAccumulatedThisSegment) || 0
        return {
            fastest,
            slowest,
            difference: fastest - slowest,
            fastestAccumulated,
            slowestAccumulated,
            differenceAccumulated: fastestAccumulated - slowestAccumulated
        }
    })
    players = players.map(player => ({
        ...player,
        segments: player.segments.map((segment, segmentIndex) => {
            const { slowest, difference, slowestAccumulated, differenceAccumulated } = fastestAndSlowerPerSegment[segmentIndex]
            return {
                ...segment,
                // ranking for this segment [0, 1] where 0 is ranked the slowest and 1 the fastest
                rankingForThisSegment: segment.time ? (segment.time - slowest) / difference : null,
                // ranking for this segment for accumulated times [0, 1] where 0 is ranked the slowest and 1 the fastest
                rankingAccumulated: segment.accumulated ? (segment.accumulated - slowestAccumulated) / differenceAccumulated : null,
            }
        })
    }))

    // Calculates the percentage for average speed for each cumulative race
    const numberOrRaces = players.length > 0 ? ((players[0].cumulative || {}).speeds || []).length : 0
    const maxAndMinAverageSpeedPerRace = range(0, numberOrRaces).map(i => {
        const averageSpeedsForThisRace = players.map(p => p.cumulative.speeds[i].average)
        const fastest = max(averageSpeedsForThisRace) || 0
        const slowest = min(averageSpeedsForThisRace) || 0
        return {
            fastest,
            slowest,
            difference: fastest - slowest
        }
    })
    players = players.map(player => ({
        ...player,
        cumulative: {
            ...player.cumulative,
            speeds: player.cumulative.speeds.map((speedsData, index) => {
                const { slowest, difference } = maxAndMinAverageSpeedPerRace[index]
                return {
                    ...speedsData,
                    percentage: speedsData.average ? ((speedsData.average - slowest) / difference) * 100 : NaN
                }
            })
        }
    }))

    return {
        ...input,
        data: {
            ...data,
            players,
            numberOfSegments,
            fastLap,
            raceTime
        }
    }
}



// WEBPACK FOOTER //
// src/utils/dataEnhancer/phases/race.js